// MARK://dhk8/pos/features/AddItemDialog
// "Personalizar y agregar" sheet for a menu product — the add-to-comanda flow.
// Replaces the bare qty+notes dialog: it now also renders a variant picker
// (single-select, price deltas — e.g. "Tamaño: Chico/Mediano/Grande") and any
// modifier groups (multi-select with min/max + required validation — e.g.
// "Término: rojo/medio/…", "Extras: queso +$10"). The live total reflects
// base + variant delta + Σ modifier deltas, times qty.
//
// Data sources:
//   • preview build → src/lib/seed-preview.ts SEED_PRODUCT_EXTRAS (variants + groups);
//   • real build    → modifier groups fetched from dhk8-catalog-modifiers (variants
//     aren't surfaced by that service yet — TODO(backend): a per-product variant read).
// Built on the BottomSheet primitive + `--pos-*` tokens — theme-aware, ≥44pt targets.

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Minus, Plus, Sparkles } from 'lucide-react'
import type { ProductDto } from '@dockethub/ui-restaurant'
import type { DraftItem } from '../stores/order-store.js'
import { formatCents } from '../lib/money.js'
import { listModifierGroups } from '../lib/clients/modifiers.js'
import { PREVIEW_STUB, previewProductExtras, type PreviewModifierGroup } from '../lib/seed-preview.js'
import BottomSheet from './pos/BottomSheet.js'

export interface AddItemDialogProps {
  product: ProductDto | null
  onClose: () => void
  onConfirm: (draft: DraftItem) => Promise<void>
}

interface SelectedMod { id: string; name: string; priceDeltaCents: number; groupId: string }

export default function AddItemDialog({ product, onClose, onConfirm }: AddItemDialogProps) {
  const extras = useMemo(() => (product ? previewProductExtras(product.id) : {}), [product])

  // Real-build fallback: pull modifier groups from the backend (skipped in preview).
  const fetchedGroups = useQuery({
    enabled: !!product && !PREVIEW_STUB,
    queryKey: ['modifiers', 'groups', product?.id],
    queryFn: async ({ signal }) => {
      if (!product) return [] as PreviewModifierGroup[]
      try {
        const r = await listModifierGroups({ productId: product.id }, signal)
        return r.items.filter((g) => g.active && !g.deletedAt).map<PreviewModifierGroup>((g) => ({
          id: g.id, name: g.name, minSelect: g.minSelect, maxSelect: g.maxSelect, isRequired: g.isRequired,
          options: g.options.filter((o) => o.active).slice().sort((a, b) => a.sortOrder - b.sortOrder)
            .map((o) => ({ id: o.id, name: o.label, priceDeltaCents: o.priceDeltaCents })),
        }))
      } catch { return [] as PreviewModifierGroup[] }
    },
    staleTime: 60_000,
  })

  const variantSet = extras.variants
  const modifierGroups: PreviewModifierGroup[] = extras.modifierGroups ?? fetchedGroups.data ?? []

  const [quantity, setQuantity] = useState(1)
  const [notes, setNotes] = useState('')
  const [variantId, setVariantId] = useState<string | null>(null)
  const [selected, setSelected] = useState<Map<string, SelectedMod>>(() => new Map())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setQuantity(1)
    setNotes('')
    setSelected(new Map())
    setError(null)
    // default-select the first variant option so the line always has a concrete variant
    setVariantId(variantSet ? (variantSet.options[0]?.id ?? null) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, variantSet])

  const variantOption = variantSet?.options.find((o) => o.id === variantId) ?? null
  const variantDelta = variantOption?.priceDeltaCents ?? 0
  const modDelta = useMemo(() => [...selected.values()].reduce((s, o) => s + o.priceDeltaCents, 0), [selected])
  // `unitPriceCents` sent to the backend must be the BASE unit price (product +
  // variant delta) ONLY — NOT the modifier deltas. dhk8-pos-orders computes
  // lineGross = (unitPriceCents + Σ modifiers[].priceDeltaCents) * quantity, so
  // folding modDelta in here would double-count every modifier (the POS was
  // over-charging). The variant delta stays in unitPriceCents because variants
  // are NOT carried in the modifiers[] array. Mirrors the OhSir guest PWA.
  const baseUnitCents = (product?.unitPriceCents ?? 0) + variantDelta
  // Full per-unit price for display only (button label / live total).
  const displayUnitCents = baseUnitCents + modDelta
  const lineTotalCents = displayUnitCents * quantity

  function toggleMod(group: PreviewModifierGroup, opt: { id: string; name: string; priceDeltaCents: number }) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(opt.id)) { next.delete(opt.id); return next }
      const inGroup = [...next.values()].filter((o) => o.groupId === group.id)
      if (group.maxSelect === 1) { for (const e of inGroup) next.delete(e.id) }
      else if (inGroup.length >= group.maxSelect) return prev
      next.set(opt.id, { ...opt, groupId: group.id })
      return next
    })
  }

  function validate(): string | null {
    if (variantSet && !variantId) return `Selecciona una opción en "${variantSet.name}"`
    for (const g of modifierGroups) {
      const count = [...selected.values()].filter((o) => o.groupId === g.id).length
      const min = g.isRequired ? Math.max(1, g.minSelect) : g.minSelect
      if (count < min) return `Selecciona al menos ${min} en "${g.name}"`
      if (count > g.maxSelect) return `Máximo ${g.maxSelect} en "${g.name}"`
    }
    return null
  }

  async function handleConfirm() {
    if (!product) return
    const v = validate()
    if (v) { setError(v); return }
    setBusy(true)
    setError(null)
    try {
      const trimmed = notes.trim()
      const draft: DraftItem = {
        productId: product.id,
        name: product.name,
        // BASE unit price (product + variant) only — the backend adds the
        // modifier deltas from `modifiers[]`. See baseUnitCents above.
        unitPriceCents: baseUnitCents,
        taxRate: product.taxRate,
        quantity,
        modifiers: [...selected.values()].map((o) => ({ modifierId: o.id, name: o.name, priceDeltaCents: o.priceDeltaCents })),
        ...(variantOption ? { variantId: variantOption.id, variantName: `${variantSet?.name}: ${variantOption.name}` } : {}),
        ...(trimmed ? { notes: trimmed } : {}),
      }
      await onConfirm(draft)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo agregar el ítem')
    } finally {
      setBusy(false)
    }
  }

  if (!product) return null
  const customizable = !!variantSet || modifierGroups.length > 0

  return (
    <BottomSheet
      open
      onClose={() => !busy && onClose()}
      title={
        <span className="dhk8-aid__head">
          {product.name}
          {customizable && <span className="dhk8-aid__badge"><Sparkles size={11} /> Personalizable</span>}
        </span>
      }
      heightFraction={0.86}
      footer={
        <div className="dhk8-aid__foot">
          <button type="button" className="dhk8-aid__btn dhk8-aid__btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="dhk8-aid__btn dhk8-aid__btn--primary" onClick={() => void handleConfirm()} disabled={busy}>
            {busy ? 'Agregando…' : `Agregar · ${formatCents(lineTotalCents)}`}
          </button>
        </div>
      }
    >
      <div className="dhk8-aid">
        <div className="dhk8-aid__qty">
          <span className="dhk8-aid__label">Cantidad</span>
          <div className="dhk8-aid__stepper">
            <button type="button" className="dhk8-aid__step" aria-label="Disminuir cantidad" disabled={quantity <= 1} onClick={() => setQuantity((q) => Math.max(1, q - 1))}><Minus size={18} /></button>
            <span className="dhk8-aid__step-val" aria-live="polite">{quantity}</span>
            <button type="button" className="dhk8-aid__step" aria-label="Aumentar cantidad" disabled={quantity >= 99} onClick={() => setQuantity((q) => Math.min(99, q + 1))}><Plus size={18} /></button>
          </div>
        </div>

        {variantSet && (
          <div className="dhk8-aid__group">
            <div className="dhk8-aid__group-head">
              <span className="dhk8-aid__group-name">{variantSet.name}</span>
              <span className="dhk8-aid__group-meta">Elige 1 · requerido</span>
            </div>
            <div className="dhk8-aid__opts">
              {variantSet.options.map((o) => {
                const on = variantId === o.id
                return (
                  <button key={o.id} type="button" className={`dhk8-aid__opt${on ? ' dhk8-aid__opt--on' : ''}`} onClick={() => setVariantId(o.id)}>
                    <span className="dhk8-aid__opt-name">{o.name}</span>
                    {o.priceDeltaCents !== 0 && <span className="dhk8-aid__opt-delta">{o.priceDeltaCents > 0 ? '+' : '−'}{formatCents(Math.abs(o.priceDeltaCents))}</span>}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {modifierGroups.map((g) => {
          const count = [...selected.values()].filter((o) => o.groupId === g.id).length
          return (
            <div key={g.id} className="dhk8-aid__group">
              <div className="dhk8-aid__group-head">
                <span className="dhk8-aid__group-name">{g.name}</span>
                <span className="dhk8-aid__group-meta">
                  {g.isRequired ? 'Requerido · ' : ''}
                  {g.maxSelect === 1 ? 'Elige 1' : g.minSelect === g.maxSelect ? `Elige ${g.maxSelect}` : `Hasta ${g.maxSelect}`}
                  {count > 0 ? ` · ${count} sel.` : ''}
                </span>
              </div>
              <div className="dhk8-aid__opts">
                {g.options.map((o) => {
                  const on = selected.has(o.id)
                  return (
                    <button key={o.id} type="button" className={`dhk8-aid__opt${on ? ' dhk8-aid__opt--on' : ''}`} onClick={() => toggleMod(g, o)}>
                      <span className="dhk8-aid__opt-name">{o.name}</span>
                      {o.priceDeltaCents !== 0 && <span className="dhk8-aid__opt-delta">+{formatCents(o.priceDeltaCents)}</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}

        <div className="dhk8-aid__group">
          <span className="dhk8-aid__label">Notas para cocina</span>
          <textarea className="dhk8-aid__textarea" rows={2} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder="Ej. sin cebolla, salsa aparte…" />
        </div>

        {error && <div className="dhk8-aid__alert">{error}</div>}
      </div>
      <style>{styles}</style>
    </BottomSheet>
  )
}

const styles = `
  .dhk8-aid__head { display:inline-flex; align-items:center; gap:var(--pos-space-2,8px); flex-wrap:wrap; }
  .dhk8-aid__badge { display:inline-flex; align-items:center; gap:3px; padding:1px var(--pos-space-2,8px); border-radius:var(--pos-radius-pill,999px); background:var(--pos-tint-soft,rgba(31,139,58,.12)); color:var(--pos-tint,#1F8B3A); font-size:9px; font-weight:var(--pos-weight-semibold,600); letter-spacing:.06em; text-transform:uppercase; }
  .dhk8-aid { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-aid__label { font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-aid__qty { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-3,12px); }
  .dhk8-aid__stepper { display:flex; align-items:center; gap:var(--pos-space-3,12px); }
  .dhk8-aid__step { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-aid__step:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-aid__step:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-aid__step-val { min-width:44px; text-align:center; font-size:var(--pos-text-2xl,24px); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); }
  .dhk8-aid__group { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); padding-top:var(--pos-space-3,12px); border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); }
  .dhk8-aid__group:first-child { padding-top:0; border-top:none; }
  .dhk8-aid__group-head { display:flex; align-items:baseline; justify-content:space-between; gap:var(--pos-space-2,8px); }
  .dhk8-aid__group-name { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-medium,500); color:var(--pos-fg,#E8EFE9); }
  .dhk8-aid__group-meta { font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); white-space:nowrap; }
  .dhk8-aid__opts { display:grid; grid-template-columns:repeat(2,1fr); gap:var(--pos-space-2,8px); }
  .dhk8-aid__opt { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-2,8px); min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg,#E8EFE9); font:inherit; text-align:left; cursor:pointer; transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-aid__opt:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-aid__opt--on { background:var(--pos-tint-soft,rgba(31,139,58,.12)); border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-aid__opt-name { font-size:var(--pos-text-sm,13px); line-height:1.2; }
  .dhk8-aid__opt-delta { flex-shrink:0; font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-xxs,11px); font-variant-numeric:tabular-nums; color:var(--pos-tint,#1F8B3A); }
  .dhk8-aid__textarea { width:100%; min-height:64px; padding:var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-md,15px); line-height:1.4; resize:vertical; outline:none; transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-aid__textarea:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-aid__textarea::placeholder { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-aid__alert { color:var(--pos-danger,#E5484D); background:color-mix(in oklch, var(--pos-danger,#E5484D) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }
  .dhk8-aid__foot { display:flex; gap:var(--pos-space-3,12px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-aid__btn { flex:1; min-height:var(--pos-touch-target,44px); display:inline-flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-md,8px); font:inherit; font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-aid__btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-aid__btn--ghost { border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg,#E8EFE9); }
  .dhk8-aid__btn--ghost:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-aid__btn--primary { border:1px solid var(--pos-tint,#1F8B3A); background:var(--pos-tint,#1F8B3A); color:#fff; font-weight:var(--pos-weight-semibold,600); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-aid__btn--primary:hover:not(:disabled) { filter:brightness(1.06); }
`
